import React, { useEffect, useId, useRef } from 'react';
import { createRoot } from 'react-dom/client';

// The site's yes/no question (issues 1266–1268, 1281), in the page instead of
// the browser's window.confirm(). A native confirm can't be styled, can't be
// seen by an automated tester (its browser dismisses one as Cancel), and
// blocks the whole tab; this one looks like the site's other modals.
//
// One call, no provider: `if (!(await confirmDialog('Delete it?'))) return;`.
// The dialog mounts on its own root under <body> for the time it is open, so
// any component (or a hook outside the tree) can ask without wiring. The
// theme rides on <html>, so the tokens still apply there.
//
// Escape, a click on the backdrop and Cancel all answer no; Enter on the
// focused confirm button answers yes. Focus starts on the confirm button and
// goes back to whatever had it when the question was asked.
//
// `beforeunload` (leaving the site, reloading) stays native: browsers allow
// nothing else there.

export const ConfirmDialog = ({ message, confirmLabel = 'OK', cancelLabel = 'Cancel', onAnswer }) => {
  const messageId = useId();
  const confirmRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    if (confirmRef.current) confirmRef.current.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onAnswer(false); return; }
      if (e.key !== 'Tab' || !cardRef.current) return;
      // Keep Tab inside the two buttons while the question is open.
      const buttons = cardRef.current.querySelectorAll('button');
      if (!buttons.length) return;
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    // Capture, so a page's own Escape handler (an open modal underneath)
    // doesn't also act on the key that answered this question.
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onAnswer]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onAnswer(false); }}
      data-testid="confirm-dialog"
    >
      <div
        ref={cardRef}
        className="card w-full max-w-md space-y-4"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={messageId}
      >
        <p id={messageId} className="[overflow-wrap:anywhere]" style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{message}</p>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={() => onAnswer(false)}>{cancelLabel}</button>
          <button type="button" className="btn btn-primary" ref={confirmRef} onClick={() => onAnswer(true)}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Ask a yes/no question in the page. Resolves true for the confirm button,
 * false for Cancel, Escape or a click outside.
 */
export function confirmDialog(message, { confirmLabel = 'OK', cancelLabel = 'Cancel' } = {}) {
  return new Promise((resolve) => {
    const returnFocus = document.activeElement;
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    let answered = false;
    const onAnswer = (yes) => {
      if (answered) return;
      answered = true;
      root.unmount();
      host.remove();
      if (returnFocus && typeof returnFocus.focus === 'function' && document.contains(returnFocus)) {
        returnFocus.focus();
      }
      resolve(yes);
    };
    root.render(
      <ConfirmDialog message={message} confirmLabel={confirmLabel} cancelLabel={cancelLabel} onAnswer={onAnswer} />,
    );
  });
}

/** The same function, for a component that prefers a hook. */
export const useConfirm = () => confirmDialog;

export default confirmDialog;
